import React, { useState, useEffect, useMemo } from 'react';
import { Download, X, Smartphone } from 'lucide-react';

const PWAInstallBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  
  // Detectar iOS y standalone fuera del effect para evitar re-renders
  const isIOS = useMemo(() => {
    return typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);
  }, []);
  
  const isStandalone = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia('(display-mode: standalone)').matches || 
           window.navigator.standalone === true;
  }, []);

  useEffect(() => {
    // Si ya está instalada, no hacer nada
    if (isStandalone) return;

    // Verificar si ya se ha descartado el banner
    const dismissed = localStorage.getItem('pwa-banner-dismissed');
    if (dismissed && Date.now() - parseInt(dismissed) < 7 * 24 * 60 * 60 * 1000) {
      // No mostrar si fue descartado en los últimos 7 días
      return;
    }

    // Evento para Android/Chrome
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Mostrar banner para iOS si no está instalada
    let timeoutId;
    if (isIOS) {
      timeoutId = setTimeout(() => setShowBanner(true), 3000);
    }

    // Evento de app instalada
    const handleAppInstalled = () => {
      setShowBanner(false);
      setDeferredPrompt(null);
      console.log('[PWA] App instalada exitosamente');
    };

    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isIOS, isStandalone]);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      // Android/Chrome - mostrar prompt nativo
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('[PWA] Resultado de instalación:', outcome);
      if (outcome === 'accepted') {
        setShowBanner(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowBanner(false);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  };

  if (isStandalone || !showBanner) {
    return null;
  }

  return (
    <div className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96 animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 shadow-2xl">
        <button 
          onClick={handleDismiss}
          className="absolute top-2 right-2 text-zinc-500 hover:text-zinc-300 transition-colors"
          aria-label="Cerrar"
        >
          <X size={18} />
        </button>
        
        <div className="flex items-start gap-3">
          <div className="p-2 bg-zinc-800 rounded-lg">
            <Smartphone className="h-6 w-6 text-white" />
          </div>
          
          <div className="flex-1 pr-4">
            <h3 className="text-white font-semibold text-sm mb-1">
              Instalar ATELIER JPM
            </h3>
            
            {isIOS ? (
              <p className="text-zinc-400 text-xs leading-relaxed">
                Toca <span className="inline-flex items-center"><svg className="w-4 h-4 mx-1" fill="currentColor" viewBox="0 0 24 24"><path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/></svg></span> 
                y luego <strong>&quot;Agregar a pantalla de inicio&quot;</strong> para instalar la app.
              </p>
            ) : (
              <p className="text-zinc-400 text-xs leading-relaxed">
                Instala la app en tu dispositivo para acceso rápido y uso offline.
              </p>
            )}
          </div>
        </div>
        
        {!isIOS && deferredPrompt && (
          <button
            onClick={handleInstallClick}
            className="mt-3 w-full flex items-center justify-center gap-2 bg-white text-zinc-900 hover:bg-zinc-100 font-medium py-2 px-4 rounded-lg transition-colors text-sm"
          >
            <Download size={16} />
            Instalar ahora
          </button>
        )}
        
        {isIOS && (
          <div className="mt-3 pt-3 border-t border-zinc-800">
            <div className="flex items-center gap-2 text-xs text-zinc-500">
              <span className="flex items-center gap-1">
                <span className="w-5 h-5 rounded bg-zinc-800 flex items-center justify-center text-white text-[10px] font-bold">1</span>
                Toca compartir
              </span>
              <span className="text-zinc-600">→</span>
              <span className="flex items-center gap-1">
                <span className="w-5 h-5 rounded bg-zinc-800 flex items-center justify-center text-white text-[10px] font-bold">2</span>
                Agregar a inicio
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PWAInstallBanner;
